import {
  GoogleIntegrationStatus,
  MessagingPlatform,
  MicrosoftIntegrationStatus,
} from '@this+that/models';
import log from 'loglevel';

import { IntegrationAccount } from '../../models/integrationAccount';
import { useGetUserGoogleIntegrationStatus } from '../../services/google.service';
import { useGetUserMicrosoftIntegrationStatus } from '../../services/microsoft.service';
import { useIntegrationsStore } from '../../store';
import { useFetchCacheStore } from '../../store/FetchCache';

const buildGoogleAccounts = (
  googleIntegrations: GoogleIntegrationStatus[],
): IntegrationAccount[] =>
  googleIntegrations
    .filter((i) => Boolean(i.gmailAddress) && i.gmailEnabled)
    .map((i) => ({
      platform: MessagingPlatform.GMAIL,
      identifier: i.gmailAddress as string,
      displayName: i.gmailAddress as string,
    }));

const buildMicrosoftAccounts = (
  microsoftIntegrations: MicrosoftIntegrationStatus[] | undefined,
): IntegrationAccount[] => {
  if (!microsoftIntegrations) {
    return [];
  }
  return microsoftIntegrations
    .filter(
      (i) =>
        Boolean(i.homeAccountId) &&
        Boolean(i.outlookAddress) &&
        i.outlookEnabled,
    )
    .map((i) => ({
      platform: MessagingPlatform.OUTLOOK,
      identifier: i.homeAccountId,
      displayName: i.outlookAddress,
    }));
};

const writeIntegrations = (
  googleIntegrations: GoogleIntegrationStatus[] | null,
  microsoftIntegrations: MicrosoftIntegrationStatus[] | null,
) => {
  const state = useIntegrationsStore.getState();

  if (googleIntegrations) {
    state.setUserGoogleIntegrations(googleIntegrations);
  }
  if (microsoftIntegrations) {
    state.setUserMicrosoftIntegrations(microsoftIntegrations);
  }

  state.setIntegrationAccounts([
    ...buildGoogleAccounts(googleIntegrations ?? state.userGoogleIntegrations),
    ...buildMicrosoftAccounts(
      microsoftIntegrations ?? state.userMicrosoftIntegrations,
    ),
  ]);
};

type StatusGetters = {
  getGoogle: () => Promise<GoogleIntegrationStatus[] | null>;
  getMicrosoft: () => Promise<MicrosoftIntegrationStatus[] | null>;
};

let inFlightStatusFetch: Promise<boolean> | null = null;

const fetchStatuses = async (
  { getGoogle, getMicrosoft }: StatusGetters,
  forceRefresh: boolean,
): Promise<boolean> => {
  try {
    const [googleIntegrations, microsoftIntegrations] = await Promise.all([
      getGoogle(),
      getMicrosoft(),
    ]);

    if (googleIntegrations === null && microsoftIntegrations === null) {
      log.error('Integration status fetch failed; keeping previous state');
      return false;
    }

    writeIntegrations(googleIntegrations, microsoftIntegrations);

    if (googleIntegrations === null || microsoftIntegrations === null) {
      log.error('Integration status fetch partially failed');
      return false;
    }

    useFetchCacheStore.getState().markIntegrationsAsLoaded();
    if (forceRefresh) {
      useFetchCacheStore.getState().invalidateLabels();
    }
    return true;
  } catch (error) {
    log.error(error);
    return false;
  } finally {
    useIntegrationsStore.getState().setLoading(false);
  }
};

export const useGetIntegrations = () => {
  const { getUserGoogleIntegrationStatus } =
    useGetUserGoogleIntegrationStatus();
  const { getUserMicrosoftIntegrationStatus } =
    useGetUserMicrosoftIntegrationStatus();

  const handleGetIntegrations = async (forceRefresh = false) => {
    if (!forceRefresh) {
      if (useFetchCacheStore.getState().isIntegrationsLoaded()) {
        return true;
      }
      if (inFlightStatusFetch) {
        return inFlightStatusFetch;
      }
    }

    const request: Promise<boolean> = fetchStatuses(
      {
        getGoogle: getUserGoogleIntegrationStatus,
        getMicrosoft: getUserMicrosoftIntegrationStatus,
      },
      forceRefresh,
    ).finally(() => {
      if (inFlightStatusFetch === request) {
        inFlightStatusFetch = null;
      }
    });
    inFlightStatusFetch = request;

    return request;
  };

  return { handleGetIntegrations };
};
